import { supabase } from "./supabase";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";

/**
 * Uploads a file to the specified Supabase storage bucket
 */
export const uploadFile = async (
  file: File,
  bucket: string = "user-files",
  path: string = "",
  metadata: Record<string, string> = {},
): Promise<string | null> => {
  try {
    // Generate a unique file path to avoid collisions
    const fileExt = file.name.split(".").pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = path ? `${path}/${fileName}` : fileName;

    // Get current user to ensure we have authentication
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Du måste vara inloggad", {
        description: "Du behöver vara inloggad för att ladda upp filer.",
      });
      return null;
    }

    console.log(`Attempting to upload file to path: ${filePath}`);

    // Upload the file to Supabase storage
    const { data, error } = await supabase.storage.from(bucket).upload(filePath, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type,
      metadata,
    });

    if (error) {
      console.error("Error uploading file:", error);

      toast.error("Kunde inte ladda upp filen", {
        description: error.message,
      });

      // Additional debugging
      console.error(`Path: ${path}`);
      console.error(`User ID: ${user.id}`);
      console.error(`Full error: ${JSON.stringify(error)}`);

      return null;
    }

    console.log("File uploaded successfully:", data.path);
    return data.path;
  } catch (error) {
    console.error("Unexpected error during file upload:", error);
    toast.error("Ett oväntat fel inträffade vid filuppladdning");
    return null;
  }
};

/**
 * Gets a public URL for a file in Supabase storage
 */
export const getFileUrl = (filePath: string, bucket: string = "user-files"): string | null => {
  try {
    const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
    return data.publicUrl;
  } catch (error) {
    console.error("Error getting file URL:", error);
    return null;
  }
};

/**
 * Finds the public URL for a project's image in the `project-images` bucket.
 */
export const findProjectImageUrl = async (projectId: string): Promise<string | null> => {
  const { data, error } = await supabase.storage
    .from("project-images")
    .list("", { search: projectId, limit: 5 });

  if (error || !data?.length) return null;

  // `search` is a prefix match, so we verify the stem matches exactly
  const match = data.find((file) => file.name.startsWith(`${projectId}.`));
  if (!match) return null;

  return getFileUrl(match.name, "project-images");
};

/**
 * Deletes a file from Supabase storage
 */
export const deleteFile = async (
  filePath: string,
  bucket: string = "user-files",
): Promise<boolean> => {
  try {
    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Du måste vara inloggad", {
        description: "Du behöver vara inloggad för att ta bort filer.",
      });
      return false;
    }

    const { error } = await supabase.storage.from(bucket).remove([filePath]);

    if (error) {
      console.error("Error deleting file:", error);

      toast.error("Kunde inte ta bort filen", {
        description: error.message,
      });
      return false;
    }

    return true;
  } catch (error) {
    console.error("Unexpected error during file deletion:", error);
    toast.error("Ett oväntat fel inträffade vid borttagning av filen");
    return false;
  }
};

// Report Images bucket constants and helpers
const REPORT_IMAGES_BUCKET = "report-images";

/**
 * Uploads an image to the report-images bucket
 * @param file The image file to upload
 * @param documentId The canvas document ID (used as folder path)
 * @returns The storage path, or null on failure
 */
export const uploadReportImage = async (file: File, documentId: string): Promise<string | null> => {
  return uploadFile(file, REPORT_IMAGES_BUCKET, documentId);
};

/**
 * Gets the authenticated URL for a report image (creates signed URL)
 * @param filePath The file path within the bucket
 * @returns The signed URL or null on failure
 */
export const getReportImageUrl = async (filePath: string): Promise<string | null> => {
  const { data, error } = await supabase.storage
    .from(REPORT_IMAGES_BUCKET)
    .createSignedUrl(filePath, 3600); // 1 hour expiry

  if (error) {
    console.error("Error getting signed URL:", error);
    return null;
  }
  return data.signedUrl;
};

/**
 * Deletes a report image
 * @param filePath The file path within the bucket
 * @returns True if deletion was successful
 */
export const deleteReportImage = async (filePath: string): Promise<boolean> => {
  return deleteFile(filePath, REPORT_IMAGES_BUCKET);
};

/**
 * Creates Supabase storage bucket if it doesn't exist
 * Note: This requires admin privileges and should be done via migrations
 */
export const createStorageBucket = async (
  bucketName: string,
  isPublic: boolean = false,
): Promise<boolean> => {
  try {
    // Check if bucket exists
    const { data: buckets } = await supabase.storage.listBuckets();
    const bucketExists = buckets?.some((bucket) => bucket.name === bucketName);

    if (!bucketExists) {
      const { error } = await supabase.storage.createBucket(bucketName, {
        public: isPublic,
        fileSizeLimit: 5242880, // 5MB
      });

      if (error) {
        console.error("Error creating bucket:", error);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error("Error creating storage bucket:", error);
    return false;
  }
};
